/**
 * AdminHeritagePage ✦ iter233 — /admin/heritage
 *
 * Full CRUD for the Heritage Collection surfaced on
 * /photobooth-hire. Two tabs:
 *   • Products — name, description, features[], display order,
 *     active toggle, upload/replace/clear cover photo, + add new
 *   • Packages — name, includes[], price, status (coming-soon /
 *     live), display order, active toggle, + add new
 *
 * Mirrors iter230 backdrops admin shape so the surface reads
 * familiar.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const GOLD_HAIRLINE = 'rgba(212,170,82,0.14)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';
const IVORY_GHOST = 'rgba(251,238,208,0.42)';

const mediaUrl = (path) =>
  path?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${path}` : path;

const pageStyle = {
  minHeight: '100vh', background: '#0F0B1A', color: IVORY,
  fontFamily: SERIF, padding: 'clamp(40px, 6vw, 70px) clamp(16px, 3vw, 40px) 80px',
};
const inputBase = {
  background: 'rgba(0,0,0,0.34)', border: `1px solid ${GOLD_FAINT}`,
  color: IVORY, fontFamily: SERIF, fontSize: 13.5,
  padding: '8px 10px', borderRadius: 6, width: '100%',
  boxSizing: 'border-box',
};
const ghostPillStyle = {
  background: 'transparent', border: `1px solid ${GOLD_FAINT}`,
  color: IVORY, fontFamily: SERIF, fontSize: 12.5,
  padding: '6px 14px', borderRadius: 999, cursor: 'pointer',
  letterSpacing: '0.02em',
};
const primaryPillStyle = {
  ...ghostPillStyle,
  background: 'rgba(212,170,82,0.14)',
};
const labelStyle = {
  display: 'block', fontFamily: SERIF, fontStyle: 'italic',
  color: IVORY_DIM, fontSize: 11.5, letterSpacing: '0.04em',
  marginBottom: 4,
};

const AdminHeritagePage = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [tab, setTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [packagesSectionVisible, setPackagesSectionVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (auth?.user === null) navigate('/login?next=/admin/heritage');
  }, [auth?.user, navigate]);

  const load = useCallback(async () => {
    try {
      const [pRes, kRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/heritage/products`),
        axios.get(`${API}/admin/heritage/packages`),
        axios.get(`${API}/admin/heritage/packages-section`),
      ]);
      setProducts(pRes.data.products || []);
      setPackages(kRes.data.packages || []);
      setPackagesSectionVisible(sRes.data.visible !== false);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error('Could not load Heritage Collection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // iter233b — section-level visibility toggle. Hides the whole
  // packages strip on /photobooth-hire without touching individual
  // package Active flags.
  const togglePackagesSection = async () => {
    const next = !packagesSectionVisible;
    setPackagesSectionVisible(next);
    try {
      await axios.patch(`${API}/admin/heritage/packages-section`, { visible: next });
      toast.success(next ? 'Packages section visible.' : 'Packages section hidden.');
    } catch (e) {
      setPackagesSectionVisible(!next);
      toast.error(e?.response?.data?.detail || 'Toggle failed.');
    }
  };

  if (loading) {
    return (
      <div style={pageStyle}>
        <p style={{ fontStyle: 'italic', color: IVORY_DIM, textAlign: 'center', marginTop: 80 }}>
          Loading Heritage Collection ✦
        </p>
      </div>
    );
  }

  return (
    <div data-testid="admin-heritage-page" style={pageStyle}>
      <div style={{ maxWidth: 1080, margin: '0 auto' }}>
        <header style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          gap: 16, flexWrap: 'wrap', marginBottom: 22,
        }}>
          <div>
            <div style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
              color: GOLD, marginBottom: 8,
            }}>✦ Heritage Collection</div>
            <h1 style={{
              fontFamily: SERIF, fontWeight: 500,
              fontSize: 'clamp(26px, 4vw, 36px)', margin: 0,
              letterSpacing: '-0.015em',
            }}>Signature catalogue</h1>
            <p style={{
              fontFamily: SERIF, fontStyle: 'italic',
              color: IVORY_DIM, fontSize: 13.5,
              margin: '8px 0 0', maxWidth: 660, lineHeight: 1.6,
            }}>
              Edit copy, photos, ordering and live state across the Heritage Collection. Hidden items disappear from <span style={{ color: IVORY }}>/photobooth-hire</span> instantly.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button type="button" data-testid="heritage-new"
              onClick={() => setCreating(true)}
              style={primaryPillStyle}>+ Add new {tab === 'products' ? 'product' : 'package'}</button>
            <button type="button" onClick={() => navigate('/admin')}
              data-testid="admin-back-link" style={ghostPillStyle}>← Admin home</button>
          </div>
        </header>

        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 24, borderBottom: `1px solid ${GOLD_HAIRLINE}` }}>
          {['products', 'packages'].map(t => (
            <button key={t} type="button"
              data-testid={`heritage-tab-${t}`}
              onClick={() => { setTab(t); setCreating(false); }}
              style={{
                background: 'transparent', border: 'none',
                color: tab === t ? GOLD : IVORY_DIM,
                fontFamily: SERIF, fontSize: 14, letterSpacing: '0.04em',
                padding: '10px 16px', cursor: 'pointer',
                borderBottom: tab === t ? `2px solid ${GOLD}` : '2px solid transparent',
                textTransform: 'capitalize',
              }}>{t} ({t === 'products' ? products.length : packages.length})</button>
          ))}
        </div>

        {creating && tab === 'products' && (
          <NewProductForm onCancel={() => setCreating(false)}
            onCreated={async () => { setCreating(false); await load(); }} />
        )}
        {creating && tab === 'packages' && (
          <NewPackageForm onCancel={() => setCreating(false)}
            onCreated={async () => { setCreating(false); await load(); }} />
        )}

        {tab === 'products' && (
          <div style={{ display: 'grid', gap: 14 }}>
            {products.map(p => <ProductRow key={p.slug} product={p} onChanged={load} />)}
            {products.length === 0 && (
              <p style={{ fontStyle: 'italic', color: IVORY_DIM, textAlign: 'center', padding: 40 }}>
                No products yet. Click &quot;+ Add new product&quot; to start.
              </p>
            )}
          </div>
        )}
        {tab === 'packages' && (
          <div style={{ display: 'grid', gap: 14 }}>
            <div data-testid="heritage-packages-section-toggle" style={{
              background: packagesSectionVisible ? 'rgba(85,107,47,0.10)' : 'rgba(184,111,111,0.10)',
              border: `1px solid ${packagesSectionVisible ? 'rgba(85,107,47,0.34)' : 'rgba(184,111,111,0.34)'}`,
              borderRadius: 10, padding: '14px 18px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 12, flexWrap: 'wrap',
            }}>
              <div>
                <div style={{
                  fontFamily: SERIF, fontSize: 14, color: IVORY, marginBottom: 4,
                }}>
                  Section visibility:{' '}
                  <span style={{ color: packagesSectionVisible ? GOLD : '#E6B5B5', fontWeight: 600 }}>
                    {packagesSectionVisible ? '✦ Showing' : '✕ Hidden'}
                  </span>
                </div>
                <div style={{
                  fontFamily: SERIF, fontStyle: 'italic',
                  color: IVORY_DIM, fontSize: 12.5, lineHeight: 1.5,
                }}>
                  When hidden, the entire <span style={{ color: IVORY }}>&quot;Heritage Collection Packages&quot;</span> strip disappears from <span style={{ color: IVORY }}>/photobooth-hire</span> — even active packages won&apos;t show. Individual Active flags below are preserved either way.
                </div>
              </div>
              <button type="button" onClick={togglePackagesSection}
                data-testid="heritage-packages-section-button"
                style={{
                  ...primaryPillStyle,
                  background: packagesSectionVisible
                    ? 'rgba(184,111,111,0.18)'
                    : 'rgba(85,107,47,0.22)',
                  borderColor: packagesSectionVisible
                    ? 'rgba(184,111,111,0.42)'
                    : 'rgba(85,107,47,0.5)',
                  whiteSpace: 'nowrap',
                }}>
                {packagesSectionVisible ? 'Hide section from customers' : 'Show section to customers'}
              </button>
            </div>
            {packages.map(p => <PackageRow key={p.slug} pkg={p} onChanged={load} />)}
            {packages.length === 0 && (
              <p style={{ fontStyle: 'italic', color: IVORY_DIM, textAlign: 'center', padding: 40 }}>
                No packages yet. Click &quot;+ Add new package&quot; to start.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Product row ────────────────────────────────────────────────────
const ProductRow = ({ product, onChanged }) => {
  const [form, setForm] = useState({
    name: product.name || '',
    description: product.description || '',
    features: (product.features || []).join('\n'),
    display_order: product.display_order ?? 0,
    is_active: product.is_active !== false,
    is_featured: !!product.is_featured,
    // iter236c — empty string while editing so number input behaves
    // (typing/erasing); coerced on save.
    price_from: product.price_from ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const upd = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  // iter233a — toggling Active auto-saves so it matches the user's
  // expectation ("I unchecked and it hid"). Other fields still
  // require explicit Save so accidental keypresses don't leak live.
  const toggleActive = async () => {
    const next = !form.is_active;
    setForm((f) => ({ ...f, is_active: next }));
    try {
      await axios.patch(`${API}/admin/heritage/products/${product.slug}`,
        { is_active: next });
      toast.success(next ? 'Product visible.' : 'Product hidden.');
      await onChanged();
    } catch (e) {
      setForm((f) => ({ ...f, is_active: !next }));
      toast.error(e?.response?.data?.detail || 'Toggle failed.');
    }
  };

  // iter236c — Featured toggle also auto-saves so promoting a
  // product to the hero card is instant. Only one product *should*
  // be featured at a time visually, but the backend doesn't enforce
  // that — Craig decides.
  const toggleFeatured = async () => {
    const next = !form.is_featured;
    setForm((f) => ({ ...f, is_featured: next }));
    try {
      await axios.patch(`${API}/admin/heritage/products/${product.slug}`,
        { is_featured: next });
      toast.success(next ? '★ Marked as featured.' : 'Featured flag removed.');
      await onChanged();
    } catch (e) {
      setForm((f) => ({ ...f, is_featured: !next }));
      toast.error(e?.response?.data?.detail || 'Toggle failed.');
    }
  };

  const dirty = (
    form.name !== (product.name || '') ||
    form.description !== (product.description || '') ||
    form.features !== (product.features || []).join('\n') ||
    Number(form.display_order) !== (product.display_order ?? 0) ||
    String(form.price_from) !== String(product.price_from ?? '')
    // is_active + is_featured intentionally omitted — they auto-save on toggle.
  );

  const save = async () => {
    setSaving(true);
    try {
      // iter236c — empty string → null clears the price line.
      const priceFromInt = form.price_from === '' || form.price_from === null
        ? null
        : Number(form.price_from);
      await axios.patch(`${API}/admin/heritage/products/${product.slug}`, {
        name: form.name.trim(),
        description: form.description.trim(),
        features: form.features.split('\n').map(s => s.trim()).filter(Boolean),
        display_order: Number(form.display_order) || 0,
        price_from: priceFromInt,
      });
      toast.success('Saved.');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Hide "${product.name}" from the customer page?`)) return;
    try {
      await axios.delete(`${API}/admin/heritage/products/${product.slug}`);
      toast.success('Product hidden.');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('Max 25MB per photo.'); return; }
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      await axios.post(`${API}/admin/heritage/products/${product.slug}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo uploaded.');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearPhoto = async () => {
    if (!window.confirm("Remove this product's photo?")) return;
    try {
      await axios.delete(`${API}/admin/heritage/products/${product.slug}/photo`);
      toast.success('Photo removed.');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed.');
    }
  };

  return (
    <div data-testid={`heritage-product-row-${product.slug}`} style={{
      background: 'rgba(0,0,0,0.28)', border: `1px solid ${GOLD_HAIRLINE}`,
      borderRadius: 10, padding: 18,
      opacity: form.is_active ? 1 : 0.55,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 18, alignItems: 'start' }}>
        <div>
          <div style={{
            width: 110, aspectRatio: '4 / 3',
            background: 'rgba(0,0,0,0.4)', border: `1px solid ${GOLD_HAIRLINE}`,
            borderRadius: 8, overflow: 'hidden',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {product.image_url ? (
              <img data-testid={`heritage-product-thumb-${product.slug}`}
                src={mediaUrl(product.image_url)} alt={product.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <span style={{ color: IVORY_GHOST, fontStyle: 'italic', fontSize: 11.5 }}>No photo</span>
            )}
          </div>
          <input ref={fileInputRef} type="file" accept="image/*"
            data-testid={`heritage-product-photo-input-${product.slug}`}
            onChange={(e) => uploadPhoto(e.target.files?.[0])}
            style={{ display: 'none' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
            <button type="button" disabled={uploading}
              data-testid={`heritage-product-upload-${product.slug}`}
              onClick={() => fileInputRef.current?.click()}
              style={{ ...ghostPillStyle, fontSize: 11.5, padding: '4px 8px' }}>
              {uploading ? 'Uploading…' : product.image_url ? 'Replace photo' : 'Upload photo'}
            </button>
            {product.image_url && (
              <button type="button" onClick={clearPhoto}
                data-testid={`heritage-product-clear-photo-${product.slug}`}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'rgba(184,111,111,0.78)', fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 11, cursor: 'pointer',
                }}>Clear photo</button>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Name</label>
              <input type="text" value={form.name} onChange={upd('name')}
                data-testid={`heritage-product-name-${product.slug}`} style={inputBase} />
            </div>
            <div>
              <label style={labelStyle}>Display order</label>
              <input type="number" min="0" max="9999" value={form.display_order} onChange={upd('display_order')}
                data-testid={`heritage-product-order-${product.slug}`} style={inputBase} />
            </div>
            <div>
              <label style={labelStyle}>Price from (£) — blank to hide</label>
              <input type="number" min="0" max="99999" value={form.price_from} onChange={upd('price_from')}
                placeholder="e.g. 395"
                data-testid={`heritage-product-price-from-${product.slug}`} style={inputBase} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={upd('description')} rows={3}
              data-testid={`heritage-product-description-${product.slug}`}
              style={{ ...inputBase, resize: 'vertical', minHeight: 60 }} />
          </div>
          <div>
            <label style={labelStyle}>Features — one per line</label>
            <textarea value={form.features} onChange={upd('features')} rows={4}
              data-testid={`heritage-product-features-${product.slug}`}
              placeholder={'High Quality Prints\nEasy Touch Screen Experience\nFun Props Included\nInstant Digital Copies'}
              style={{ ...inputBase, resize: 'vertical', minHeight: 80, fontFamily: 'monospace', fontSize: 12.5 }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontFamily: SERIF, fontSize: 13, color: IVORY, cursor: 'pointer',
              }}>
                <input type="checkbox" checked={!!form.is_active} onChange={toggleActive}
                  data-testid={`heritage-product-active-${product.slug}`}
                  style={{ accentColor: GOLD, width: 16, height: 16 }} />
                Active (visible) <span style={{ fontSize: 10.5, color: GOLD, marginLeft: 4 }}>✦ auto-saves</span>
              </label>
              <label style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                fontFamily: SERIF, fontSize: 13,
                color: form.is_featured ? GOLD : IVORY, cursor: 'pointer',
              }}>
                <input type="checkbox" checked={!!form.is_featured} onChange={toggleFeatured}
                  data-testid={`heritage-product-featured-${product.slug}`}
                  style={{ accentColor: GOLD, width: 16, height: 16 }} />
                ★ Featured product <span style={{ fontSize: 10.5, color: GOLD, marginLeft: 4 }}>✦ auto-saves</span>
              </label>
              <span style={{ color: IVORY_GHOST, fontStyle: 'italic', fontSize: 11.5 }}>
                slug: {product.slug}
              </span>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={remove}
                data-testid={`heritage-product-delete-${product.slug}`}
                style={{
                  background: 'transparent', border: 'none',
                  color: 'rgba(184,111,111,0.78)', fontFamily: SERIF, fontStyle: 'italic',
                  fontSize: 12.5, cursor: 'pointer',
                }}>Hide</button>
              <button type="button" onClick={save} disabled={!dirty || saving}
                data-testid={`heritage-product-save-${product.slug}`}
                style={{
                  ...primaryPillStyle,
                  opacity: dirty && !saving ? 1 : 0.4,
                  cursor: dirty && !saving ? 'pointer' : 'not-allowed',
                }}>{saving ? 'Saving…' : 'Save changes ✦'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Package row ────────────────────────────────────────────────────
const PackageRow = ({ pkg, onChanged }) => {
  const [form, setForm] = useState({
    name: pkg.name || '',
    includes: (pkg.includes || []).join('\n'),
    price: pkg.price ?? '',
    status: pkg.status || 'coming_soon',
    display_order: pkg.display_order ?? 0,
    is_active: pkg.is_active !== false,
  });
  const [saving, setSaving] = useState(false);

  const upd = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  // iter233a — Active toggle auto-saves (matches user expectation).
  const toggleActive = async () => {
    const next = !form.is_active;
    setForm((f) => ({ ...f, is_active: next }));
    try {
      await axios.patch(`${API}/admin/heritage/packages/${pkg.slug}`,
        { is_active: next });
      toast.success(next ? 'Package visible.' : 'Package hidden.');
      await onChanged();
    } catch (e) {
      setForm((f) => ({ ...f, is_active: !next }));
      toast.error(e?.response?.data?.detail || 'Toggle failed.');
    }
  };

  const dirty = (
    form.name !== (pkg.name || '') ||
    form.includes !== (pkg.includes || []).join('\n') ||
    (form.price === '' ? null : Number(form.price)) !== pkg.price ||
    form.status !== (pkg.status || 'coming_soon') ||
    Number(form.display_order) !== (pkg.display_order ?? 0)
    // is_active intentionally omitted — auto-saves on toggle.
  );

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/heritage/packages/${pkg.slug}`, {
        name: form.name.trim(),
        includes: form.includes.split('\n').map(s => s.trim()).filter(Boolean),
        price: form.price === '' || form.price === null ? null : Number(form.price),
        status: form.status,
        display_order: Number(form.display_order) || 0,
      });
      toast.success('Saved.');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Hide "${pkg.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/heritage/packages/${pkg.slug}`);
      toast.success('Package hidden.');
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed.');
    }
  };

  return (
    <div data-testid={`heritage-package-row-${pkg.slug}`} style={{
      background: 'rgba(0,0,0,0.28)', border: `1px solid ${GOLD_HAIRLINE}`,
      borderRadius: 10, padding: 18,
      opacity: form.is_active ? 1 : 0.55,
    }}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 10 }}>
        <div>
          <label style={labelStyle}>Name</label>
          <input type="text" value={form.name} onChange={upd('name')}
            data-testid={`heritage-package-name-${pkg.slug}`} style={inputBase} />
        </div>
        <div>
          <label style={labelStyle}>Price (£) — blank for On request</label>
          <input type="number" min="0" max="99999" value={form.price} onChange={upd('price')}
            data-testid={`heritage-package-price-${pkg.slug}`} style={inputBase} />
        </div>
        <div>
          <label style={labelStyle}>Status</label>
          <select value={form.status} onChange={upd('status')}
            data-testid={`heritage-package-status-${pkg.slug}`}
            style={{ ...inputBase, cursor: 'pointer' }}>
            <option value="coming_soon">🚧 Coming soon</option>
            <option value="live">✦ Live</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Display order</label>
          <input type="number" min="0" max="9999" value={form.display_order} onChange={upd('display_order')}
            data-testid={`heritage-package-order-${pkg.slug}`} style={inputBase} />
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={labelStyle}>Includes — one per line</label>
        <textarea value={form.includes} onChange={upd('includes')} rows={4}
          data-testid={`heritage-package-includes-${pkg.slug}`}
          placeholder={'Welcome Arch\nTable Plan\nLanterns\nBackdrop Wall'}
          style={{ ...inputBase, resize: 'vertical', minHeight: 80, fontFamily: 'monospace', fontSize: 12.5 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
        <label style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          fontFamily: SERIF, fontSize: 13, color: IVORY, cursor: 'pointer',
        }}>
          <input type="checkbox" checked={!!form.is_active} onChange={toggleActive}
            data-testid={`heritage-package-active-${pkg.slug}`}
            style={{ accentColor: GOLD, width: 16, height: 16 }} />
          Active (visible) <span style={{ fontSize: 10.5, color: GOLD, marginLeft: 4 }}>✦ auto-saves</span>
          <span style={{ color: IVORY_GHOST, fontStyle: 'italic', fontSize: 11.5, marginLeft: 12 }}>
            slug: {pkg.slug}
          </span>
        </label>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={remove}
            data-testid={`heritage-package-delete-${pkg.slug}`}
            style={{
              background: 'transparent', border: 'none',
              color: 'rgba(184,111,111,0.78)', fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 12.5, cursor: 'pointer',
            }}>Hide</button>
          <button type="button" onClick={save} disabled={!dirty || saving}
            data-testid={`heritage-package-save-${pkg.slug}`}
            style={{
              ...primaryPillStyle,
              opacity: dirty && !saving ? 1 : 0.4,
              cursor: dirty && !saving ? 'pointer' : 'not-allowed',
            }}>{saving ? 'Saving…' : 'Save changes ✦'}</button>
        </div>
      </div>
    </div>
  );
};

// ─── New forms ──────────────────────────────────────────────────────
const NewProductForm = ({ onCancel, onCreated }) => {
  const [form, setForm] = useState({
    slug: '', name: '', description: '', features: '', display_order: 100,
  });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async () => {
    if (!form.slug.trim() || !form.name.trim()) {
      toast.error('Slug and name are required.'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/heritage/products`, {
        slug: form.slug.trim().toLowerCase(),
        name: form.name.trim(),
        description: form.description.trim(),
        features: form.features.split('\n').map(s => s.trim()).filter(Boolean),
        display_order: Number(form.display_order) || 0,
      });
      toast.success('Product added.');
      await onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create.');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="new-heritage-product-form" style={{
      background: 'rgba(212,170,82,0.04)', border: `1px dashed ${GOLD_FAINT}`,
      borderRadius: 10, padding: 18, marginBottom: 16,
    }}>
      <div style={{ fontFamily: SERIF, fontSize: 14, color: IVORY, marginBottom: 12 }}>✦ New Heritage product</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 10 }}>
        <div>
          <label style={labelStyle}>Slug (URL-safe)</label>
          <input type="text" value={form.slug} onChange={upd('slug')}
            placeholder="e.g. woodland-wedding-arch"
            data-testid="new-heritage-product-slug" style={inputBase} />
        </div>
        <div>
          <label style={labelStyle}>Name</label>
          <input type="text" value={form.name} onChange={upd('name')}
            placeholder="e.g. Woodland Wedding Arch"
            data-testid="new-heritage-product-name" style={inputBase} />
        </div>
        <div>
          <label style={labelStyle}>Display order</label>
          <input type="number" min="0" value={form.display_order} onChange={upd('display_order')}
            data-testid="new-heritage-product-order" style={inputBase} />
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={labelStyle}>Description</label>
        <textarea value={form.description} onChange={upd('description')} rows={3}
          data-testid="new-heritage-product-description"
          style={{ ...inputBase, resize: 'vertical', minHeight: 60 }} />
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={labelStyle}>Features — one per line</label>
        <textarea value={form.features} onChange={upd('features')} rows={4}
          data-testid="new-heritage-product-features"
          style={{ ...inputBase, resize: 'vertical', minHeight: 80, fontFamily: 'monospace', fontSize: 12.5 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
        <button type="button" onClick={onCancel} style={ghostPillStyle}>Cancel</button>
        <button type="button" onClick={create} disabled={saving}
          data-testid="new-heritage-product-create" style={primaryPillStyle}>
          {saving ? 'Adding…' : 'Add product ✦'}
        </button>
      </div>
      <p style={{ fontFamily: SERIF, fontStyle: 'italic', color: IVORY_GHOST, fontSize: 11.5, margin: '10px 0 0' }}>
        Photo upload is available after the product is created.
      </p>
    </div>
  );
};

const NewPackageForm = ({ onCancel, onCreated }) => {
  const [form, setForm] = useState({
    slug: '', name: '', includes: '', price: '',
    status: 'coming_soon', display_order: 100,
  });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async () => {
    if (!form.slug.trim() || !form.name.trim()) {
      toast.error('Slug and name are required.'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/heritage/packages`, {
        slug: form.slug.trim().toLowerCase(),
        name: form.name.trim(),
        includes: form.includes.split('\n').map(s => s.trim()).filter(Boolean),
        price: form.price === '' ? null : Number(form.price),
        status: form.status,
        display_order: Number(form.display_order) || 0,
      });
      toast.success('Package added.');
      await onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create.');
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="new-heritage-package-form" style={{
      background: 'rgba(212,170,82,0.04)', border: `1px dashed ${GOLD_FAINT}`,
      borderRadius: 10, padding: 18, marginBottom: 16,
    }}>
      <div style={{ fontFamily: SERIF, fontSize: 14, color: IVORY, marginBottom: 12 }}>✦ New Heritage package</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr 1fr', gap: 10 }}>
        <div>
          <label style={labelStyle}>Slug</label>
          <input type="text" value={form.slug} onChange={upd('slug')}
            placeholder="e.g. woodland-luxe"
            data-testid="new-heritage-package-slug" style={inputBase} />
        </div>
        <div>
          <label style={labelStyle}>Name</label>
          <input type="text" value={form.name} onChange={upd('name')}
            placeholder="e.g. Woodland Luxe Package"
            data-testid="new-heritage-package-name" style={inputBase} />
        </div>
        <div>
          <label style={labelStyle}>Price (£)</label>
          <input type="number" min="0" value={form.price} onChange={upd('price')}
            data-testid="new-heritage-package-price" style={inputBase} />
        </div>
        <div>
          <label style={labelStyle}>Status</label>
          <select value={form.status} onChange={upd('status')}
            data-testid="new-heritage-package-status"
            style={{ ...inputBase, cursor: 'pointer' }}>
            <option value="coming_soon">🚧 Coming soon</option>
            <option value="live">✦ Live</option>
          </select>
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={labelStyle}>Includes — one per line</label>
        <textarea value={form.includes} onChange={upd('includes')} rows={4}
          data-testid="new-heritage-package-includes"
          style={{ ...inputBase, resize: 'vertical', minHeight: 80, fontFamily: 'monospace', fontSize: 12.5 }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
        <button type="button" onClick={onCancel} style={ghostPillStyle}>Cancel</button>
        <button type="button" onClick={create} disabled={saving}
          data-testid="new-heritage-package-create" style={primaryPillStyle}>
          {saving ? 'Adding…' : 'Add package ✦'}
        </button>
      </div>
    </div>
  );
};

export default AdminHeritagePage;
